/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: LanguagesPage.tsx
 ??? Created: 2025-01-28
 ?? Description:
    Languages list page with table view, search, pagination, and actions.
    Allows viewing, creating, duplicating, editing, and deleting language overrides.

 ?? Notes:
    - Domain-scoped (multi-tenant safe)
    - Supports search and pagination
    - Shows name, sound prefix, destination, and status

 ?? Last Updated: 2025-11-07 by GPT-5 Codex
----------------------------------------------------------------*/

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, Edit, Languages as LanguagesIcon, Trash2 } from 'lucide-react';
import { ModulePageContainer } from '../../components/layouts';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import DataGridSimple from '../../components/ui/DataGridSimple';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { useNavigationStore } from '../../store/navigation';
import { deleteLanguageById, navigateToLanguageDuplicate } from './languagesActions';
import { useLanguageList } from './hooks/useLanguageList';
import { Language } from './types';

const ACTION_BUTTON_CLASSNAMES = {
  base: 'p-2 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60',
  edit: 'hover:bg-blue-100 dark:hover:bg-blue-900/30',
  duplicate: 'hover:bg-emerald-100 dark:hover:bg-emerald-900/30',
  delete: 'hover:bg-red-100 dark:hover:bg-red-900/30',
};

export default function LanguagesPage() {
  const { t } = useI18n();
  const { setCurrentPageTitle } = useNavigationStore();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotifications();

  const pageTitle = useMemo(() => t('languages.title'), [t]);

  useEffect(() => {
    setCurrentPageTitle(null);
    return () => setCurrentPageTitle(null);
  }, [setCurrentPageTitle]);

  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; language: Language | null }>({
    isOpen: false,
    language: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    data,
    loading,
    columns: baseColumns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useLanguageList();

  // Add custom renders to columns
  const columns = useMemo(
    () =>
      baseColumns.map((column) => {
        if (column.key === 'name') {
          return {
            ...column,
            render: (_value: unknown, item: Language) => (
              <span className="font-medium text-foreground">{item.name}</span>
            ),
          };
        }
        if (column.key === 'soundPrefix') {
          return {
            ...column,
            render: (_value: unknown, item: Language) => {
              const rawPrefix = (item.settings as { sound_prefix?: unknown })?.sound_prefix;
              const soundPrefix = typeof rawPrefix === 'string' && rawPrefix.trim()
                ? rawPrefix
                : 'default/us/en/callie';
              return (
                <span className="text-sm text-muted-foreground">
                  {soundPrefix}
                </span>
              );
            },
          };
        }
        if (column.key === 'destination') {
          return {
            ...column,
            render: (_value: unknown, item: Language) => {
              // Helper function to translate destination
              const translateDestination = (module: string | undefined | null, value: string | undefined | null) => {
                if (!module) return null;
                
                // Translate module name
                const moduleLabel = t(`outboundRoutes.modules.${module}.label`, { defaultValue: module });
                
                // For terminate_call, translate the cause code
                if (module === 'terminate_call' && value) {
                  const causeLabel = t(`outboundRoutes.terminateCauses.${value}.label`, { defaultValue: value });
                  return `${moduleLabel} � ${causeLabel}`;
                }
                
                // For other modules, show the value as-is
                return value ? `${moduleLabel} � ${value}` : moduleLabel;
              };

              const destination = translateDestination(item.destinationModule, item.destinationData);

              return (
                <span className="text-sm text-muted-foreground">
                  {destination || t('common:notAvailable')}
                </span>
              );
            },
          };
        }
        if (column.key === 'enabled') {
          return {
            ...column,
            render: (_value: unknown, item: Language) => (
              <span
                className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                  item.enabled
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
                }`}
              >
                {item.enabled ? t('common:enabled') : t('common:disabled')}
              </span>
            ),
          };
        }
        if (column.key === 'createdAt') {
          return {
            ...column,
            render: (_value: unknown, item: Language) => {
              const date = new Date(item.createdAt);
              const isValid = !Number.isNaN(date.getTime());
              return (
                <span className="text-sm text-muted-foreground">
                  {isValid ? date.toLocaleDateString() : t('common:notAvailable')}
                </span>
              );
            },
          };
        }
        return column;
      }),
    [baseColumns, t],
  );

  const handleCreate = () => {
    navigate('/pbx/incoming-tools/languages/new');
  };

  const handleEdit = (language: Language) => {
    navigate(`/pbx/incoming-tools/languages/${language.uuid ?? language.id}`);
  };

  const handleDuplicate = (language: Language) => {
    navigateToLanguageDuplicate(navigate, language.uuid ?? language.id);
  };

  const handleDeleteClick = useCallback((language: Language) => {
    setDeleteModal({ isOpen: true, language });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteModal.language) return;

    setIsDeleting(true);
    try {
      await deleteLanguageById(deleteModal.language.id);
      showSuccess(t('languages.messages.deleteSuccess'));
      setDeleteModal({ isOpen: false, language: null });
      refresh();
    } catch (error) {
      showError(error.message || t('languages.messages.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  }, [deleteModal.language, refresh, showError, showSuccess, t]);

  const renderActions = useCallback(
    (language: Language) => (
      <div className="flex items-center gap-2">
        <SimpleTooltip content={t('common:edit')} position="top">
          <button
            type="button"
            onClick={() => handleEdit(language)}
            aria-label={t('common:edit')}
            className={`${ACTION_BUTTON_CLASSNAMES.base} ${ACTION_BUTTON_CLASSNAMES.edit}`}
          >
            <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </button>
        </SimpleTooltip>
        <SimpleTooltip content={t('common:duplicate')} position="top">
          <button
            type="button"
            onClick={() => handleDuplicate(language)}
            aria-label={t('common:duplicate')}
            className={`${ACTION_BUTTON_CLASSNAMES.base} ${ACTION_BUTTON_CLASSNAMES.duplicate}`}
          >
            <Copy className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          </button>
        </SimpleTooltip>
        <SimpleTooltip content={t('common:delete')} position="top">
          <button
            type="button"
            onClick={() => handleDeleteClick(language)}
            aria-label={t('common:delete')}
            className={`${ACTION_BUTTON_CLASSNAMES.base} ${ACTION_BUTTON_CLASSNAMES.delete}`}
          >
            <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
          </button>
        </SimpleTooltip>
      </div>
    ),
    [handleDeleteClick, handleDuplicate, handleEdit, t],
  );

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <>
          <DataGridSimple
            data={data}
            columns={columns.map((column) => ({
              ...column,
              render:
                column.key === 'actions'
                  ? (_value, item: Language) => renderActions(item)
                  : column.render,
            }))}
            loading={loading}
            pagination={pagination}
            searchQuery={searchQuery}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSearch={handleSearch}
            onSort={handleSort}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            onColumnToggle={handleColumnToggle}
            onAdd={handleCreate}
            title={pageTitle}
            titleIcon={<LanguagesIcon className="h-5 w-5 text-primary" />}
            addButtonText={t('languages.actions.create')}
            searchPlaceholder={t('languages.searchPlaceholder')}
            emptyMessage={t('languages.noResults')}
          />

          <ConfirmationModal
            isOpen={deleteModal.isOpen}
            onClose={() => setDeleteModal({ isOpen: false, language: null })}
            onConfirm={handleDeleteConfirm}
            title={t('languages.messages.deleteTitle')}
            message={t('languages.messages.confirmDelete', {
              name: deleteModal.language?.name ?? '',
            })}
            confirmText={t('common:delete')}
            cancelText={t('common:cancel')}
            type="danger"
            isLoading={isDeleting}
          />
        </>
      }
      formView={null}
    />
  );
}

